/**
 * `window.__marg` — the debug and test API (architecture §8.1; GDD §4.15). Installed at boot step 1 when
 * `import.meta.env.DEV` or `?debug=1` (production without `debug=1` has no `window.__marg`). The root delegates to a
 * `DebugHost` the app attaches once booted; namespaces (`creature, anim, combat, explore, econ, cards, snails, dice,
 * render, ui, telemetry`) always exist as objects and are filled from each feature's `FeatureModule.debugApi(ctx)`.
 *
 * **One clock:** `pause/resume/step/setTimeScale/setCadence` drive the app `Clock`; `__marg.anim.pause/resume/step` are
 * aliases of the same functions unless the creature feature supplies its own (which must also use the root clock).
 */
import type { BusRecord, Clock, FixtureId, QualityTier, RenderStats, SaveState, ScreenDef, Viewport } from '../shared';

/** What the app exposes to the debug API (implemented in `src/app/main.ts`). */
export interface DebugHost {
  readonly version: string;
  readonly clock: Clock;
  /** Boot done + first frame + fonts settled. */
  isReady(): boolean;
  /** No navigation/transition in flight and every mounted screen idle. */
  isIdle(): boolean;
  /** Frames rendered so far (monotonic). */
  frames(): number;
  /** Runs one frame synchronously (after `step`, so the stepped instant is rendered before `step` returns). */
  renderNow(): void;
  setCadence(hz: number | null, jitter?: number): void;
  cadence(): number | null;
  timeTravel(ms: number): void;
  goto(screen: string, params?: Record<string, string | number | boolean>): Promise<void>;
  back(): void;
  screens(): ScreenDef[];
  screenState(): { id: string; params: unknown; debug: unknown } | null;
  save(): SaveState;
  seed(): string;
  storeLog(): string[];
  viewport(): Viewport;
  setSave(save: SaveState | string): void;
  loadFixture(id: FixtureId): Promise<void>;
  exportSave(): string;
  setSeed(seed: string): void;
  events(n?: number): BusRecord[];
  perf(): { fps: number; cpuMsAvg: number; cpuMsP95: number; gpuMs: number | null; slowFrames: number; heapMB: number | null };
  renderStats(): RenderStats | null;
  loseDevice(): void;
  setQuality(tier: QualityTier): void;
  hideUi(hidden: boolean): void;
  /** Extra state for tests: lite mode, losses recovered, storage mode, read-only. */
  platform(): Record<string, unknown>;
}

/** GDD §4.15 namespaces (+ `telemetry`, v1.3). */
export const NAMESPACES = ['creature', 'anim', 'combat', 'explore', 'econ', 'cards', 'snails', 'dice', 'render', 'ui', 'telemetry'] as const;

const ZERO_STATS: RenderStats = {
  fps: 0, cpuMs: 0, gpuMs: null, drawCalls: 0, triangles: 0, particles: 0, decals: 0, passes: 0, renderScale: 0, msaa: 0, textureMB: 0,
  slotsUsed: 0, propsUsed: 0, subPanels: 0, flashFramesLast1s: 0, frameBreakCount: 0, frameBreakSlots: [], contactsUsed: 0,
  smearSlots: 0, smearMaxH: 0, mirroredDraws: 0, transientWaits: 0, uploadBytes: 0,
};

export interface MargApi {
  readonly version: string;
  ready(): boolean;
  waitIdle(opts?: { frames?: number; timeoutMs?: number }): Promise<void>;
  errors(): string[];
  pause(): void; resume(): void; step(ms: number): void; setTimeScale(k: number): void;
  setCadence(hz: number | null, jitter?: number): void;
  now(): { presentMs: number; simMs: number; wallMs: number; paused: boolean; timeScale: number; cadenceHz: number | null };
  timeTravel(ms: number): void;
  goto(screen: string, params?: Record<string, string | number | boolean>): Promise<void>;
  back(): void;
  listScreens(): Array<{ id: string; route: string | null; aliases: string[]; needs3d: boolean; capture: Array<{ label: string; params: Record<string, unknown>; at?: number }> }>;
  getState(): { screen: { id: string; params: unknown; debug: unknown } | null; save: SaveState; seed: string; storeLog: string[]; viewport: Viewport };
  setSave(save: SaveState | string): void;
  loadFixture(id: FixtureId): Promise<void>;
  exportSave(): string;
  setSeed(seed: string): void;
  events(n?: number): BusRecord[];
  perf(): ReturnType<DebugHost['perf']> & RenderStats;
  loseDevice(): void;
  setQuality(tier: QualityTier): void;
  hideUi(hidden: boolean): void;
  renderer(): RenderStats | null;
  /** F1 extra: platform state (lite mode, storage mode, recovered losses). */
  platform(): Record<string, unknown>;
  [ns: string]: unknown;
}

declare global {
  interface Window { __marg?: MargApi }
}

/**
 * Installs `window.__marg` with a late-bound host. Returns `attach(host)` (call once booted) and `mount(namespaces)`
 * (call after features' `debugApi`).
 */
export function installDebugApi(opts: { version: string; errors: () => string[] }): {
  api: MargApi;
  attach(host: DebugHost): void;
  mount(ns: Record<string, Record<string, unknown>>): void;
} {
  let host: DebugHost | null = null;
  const need = (): DebugHost => {
    if (!host) throw new Error('__marg: the app is still booting (wait for __marg.ready())');
    return host;
  };
  const raf = (): Promise<void> => new Promise((r) => requestAnimationFrame(() => r()));

  const api: MargApi = {
    version: opts.version,
    ready: () => host?.isReady() ?? false,
    async waitIdle(o = {}) {
      const frames = o.frames ?? 2;
      const deadline = performance.now() + (o.timeoutMs ?? 10_000);
      while (!host || !host.isReady()) {
        if (performance.now() > deadline) throw new Error('__marg.waitIdle: timed out before ready()');
        await raf();
      }
      let start = -1;
      for (;;) {
        if (performance.now() > deadline) throw new Error(`__marg.waitIdle: not idle within ${o.timeoutMs ?? 10_000} ms`);
        if (host.isIdle()) {
          if (start < 0) start = host.frames();
          if (host.frames() - start >= frames) return;
        } else start = -1;
        await raf();
      }
    },
    errors: () => opts.errors(),
    pause: () => { need().clock.paused = true; },
    resume: () => { need().clock.paused = false; },
    step(ms: number) {
      const h = need();
      h.clock.step(ms);
      h.renderNow();
    },
    setTimeScale: (k: number) => { need().clock.timeScale = k; },
    setCadence: (hz, jitter) => need().setCadence(hz, jitter),
    now() {
      const h = need();
      return { presentMs: h.clock.presentMs, simMs: h.clock.simMs, wallMs: h.clock.wallNow(), paused: h.clock.paused, timeScale: h.clock.timeScale, cadenceHz: h.cadence() };
    },
    timeTravel: (ms) => need().timeTravel(ms),
    async goto(screen, params) {
      await need().goto(screen, params);
      await api.waitIdle();
    },
    back: () => need().back(),
    listScreens: () => need().screens().map((d) => ({
      id: d.id, route: d.route ?? null, aliases: d.aliases ?? [], needs3d: d.needs3d,
      capture: (d.capture ?? []).map((c) => ({ label: c.label, params: { ...c.params }, at: c.at })),
    })),
    getState() {
      const h = need();
      return { screen: h.screenState(), save: h.save(), seed: h.seed(), storeLog: h.storeLog(), viewport: h.viewport() };
    },
    setSave: (s) => need().setSave(s),
    loadFixture: (id) => need().loadFixture(id),
    exportSave: () => need().exportSave(),
    setSeed: (s) => need().setSeed(s),
    events: (n) => need().events(n),
    perf() {
      const h = need();
      return { ...ZERO_STATS, ...(h.renderStats() ?? {}), ...h.perf() };
    },
    loseDevice: () => need().loseDevice(),
    setQuality: (tier) => need().setQuality(tier),
    hideUi: (hidden) => need().hideUi(hidden),
    renderer: () => need().renderStats(),
    platform: () => need().platform(),
  };
  for (const ns of NAMESPACES) api[ns] = {};
  // One clock: the animation namespace aliases the root controls until (and unless) creatures supply their own.
  Object.assign(api.anim as Record<string, unknown>, { pause: api.pause, resume: api.resume, step: api.step });

  window.__marg = api;
  return {
    api,
    attach(h) { host = h; },
    mount(ns) {
      for (const [name, members] of Object.entries(ns)) {
        const cur = api[name];
        if (cur && typeof cur === 'object') Object.assign(cur as Record<string, unknown>, members);
        else api[name] = { ...members };
      }
    },
  };
}
